import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { gql } from '@apollo/client';
import Footer from '../../Footer/Footer';
import { parseDate } from '../../../utils/eventUtils';
import LoadingSpinner from '../../common/LoadingSpinner';
import ImageSlant from '../../common/image-slant';
import castleImage from '../../../assets/castle.png';
import gardensImage from '../../../assets/gardens.png';
import retailImage from '../../../assets/retail3.webp';
import playgroundImage from '../../../assets/play.png';
import { useWhatsOnSection } from '../../../hooks/useWhatsOnSection';
import accommodationImage from '../../../assets/accommodation.jpg';
import tapasImage from '../../../assets/tapas2.jpg';
import paulaImage from '../../../assets/paula.jpg';
import kgimsonImage from '../../../assets/kgimson.webp';
import christmasImage from '../../../assets/Christmas25.png';
import bannerImage from '../../../assets/banner.webp';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';

// Styled components for the event detail page
const EventContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding-top: calc(120px + env(safe-area-inset-top, 0px));

  /* Solstice mobile only — offset synced to live header height via --site-header-height */
  ${props =>
    props.$isSolstice &&
    `
    @media (max-width: 768px) {
      padding-top: calc(var(--site-header-height, 290px) + env(safe-area-inset-top, 0px));
    }
  `}
`;

const EventHero = styled.div`
  width: 100%;
  height: ${props => props.$isSolstice ? '620px' : '400px'};
  background-image: ${props => props.$isSolstice
    ? `linear-gradient(160deg, rgba(10, 14, 32, 0.3) 0%, rgba(10, 14, 32, 0.15) 40%, rgba(157, 109, 73, 0.25) 75%, rgba(10, 14, 32, 0.75) 100%), url(${props.image})`
    : `linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.7)), url(${props.image})`};
  background-size: cover;
  background-position: ${props => props.$backgroundPosition || 'center'};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: ${props => props.$isSolstice ? '0' : '3rem'};
  
  @media (max-width: 768px) {
    height: ${props => props.$isSolstice ? '480px' : '350px'};
    margin-bottom: ${props => props.$isSolstice ? '0' : '2rem'};
    align-items: flex-end;
  }
  
  @media (max-width: 576px) {
    height: ${props => props.$isSolstice ? '440px' : '300px'};
    margin-bottom: ${props => props.$isSolstice ? '0' : '1.5rem'};
    align-items: flex-end;
  }
`;

const EventHeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  padding: 3rem 5rem 3.5rem;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem 2.5rem;
  }
  
  @media (max-width: 576px) {
    padding: 1.75rem 1rem 2rem;
  }
`;

const EventHeroContentSolstice = styled(EventHeroContent)`
  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 2rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 576px) {
    padding: 1.25rem 1rem 1.75rem;
  }
`;

const EventTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 2rem;
    word-wrap: break-word;
  }
`;

const EventDate = styled.div`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    word-wrap: break-word;
  }
  
  &:before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23ffffff' viewBox='0 0 24 24'%3E%3Cpath d='M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5zm2 4h10v2H7v-2zm0 4h7v2H7v-2z'/%3E%3C/svg%3E");
    background-size: contain;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const EventLocation = styled.div`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 576px) {
    font-size: 1rem;
    word-wrap: break-word;
  }
  
  &:before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23ffffff' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z'/%3E%3C/svg%3E");
    background-size: contain;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const EventContent = styled.div`
  padding: 4rem 5rem;
  width: 100%;
  max-width: 1600px;
  margin: -60px auto 0;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 4rem;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    padding: 3rem 2rem;
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: 0;
  }
  
  @media (max-width: 576px) {
    padding: 2rem 1rem;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  width: 100%;
  box-sizing: border-box;
`;

const SideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  box-sizing: border-box;
`;

const Strapline = styled.div`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  font-style: italic;
  color: ${props => props.theme.colors.accent};
  margin: 1rem 0 2rem;
  line-height: 1.4;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(157, 109, 73, 0.3);
  
  @media (max-width: 576px) {
    font-size: 1.2rem;
    word-wrap: break-word;
  }
`;

const AboutSection = styled.section`
  margin-bottom: 3rem;
  width: 100%;
  box-sizing: border-box;
`;

const EventDescription = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 576px) {
    font-size: 1rem;
  }
  
  h2, h3, h4 {
    font-family: ${props => props.theme.fonts.main};
    color: ${props => props.theme.colors.primary};
    margin: 1.5rem 0 1rem;
    max-width: 100%;
  }
  
  p {
    margin-bottom: 1.5rem;
    max-width: 100%;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    max-width: 100%;
    
    @media (max-width: 576px) {
      padding-left: 1rem;
    }
  }
  
  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: none;
    word-break: break-word;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      display: block;
      overflow-x: auto;
      max-width: 100%;
    }
  }
  
  td, th {
    padding: 0.5rem;
    border: 1px solid #ddd;
    
    @media (max-width: 576px) {
      padding: 0.3rem;
    }
  }
  
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const MainSectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.75rem;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.6rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 2px;
    background: linear-gradient(to right, ${props => props.theme.colors.accent}, rgba(157, 109, 73, 0.3));
  }
`;

const ContentSection = styled.section`
  background: white;
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 3rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.imageRight ? '1fr 1.5fr' : '1.5fr 1fr'};
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentImage = styled.div`
  width: 100%;
  height: 350px;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  order: ${props => props.imageRight ? 2 : 1};
  
  @media (max-width: 768px) {
    height: 250px;
    order: 1;
  }
`;

const ContentText = styled.div`
  order: ${props => props.imageRight ? 1 : 2};
  
  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.8rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #333;
  }
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.5rem;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.4rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
  }
`;

const InfoSection = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background-color: ${props => props.theme.colors.offWhite};
  border-radius: 8px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
    min-width: 16px;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const InfoValue = styled.span`
  font-size: 1.1rem;
  color: #333;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
  }
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const PriceCard = styled.div`
  background-color: ${props => props.theme.colors.offWhite};
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;
  
  @media (max-width: 576px) {
    padding: 1rem;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  }
`;

const PriceType = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
  }
`;

const Price = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin: 0.5rem 0;
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const BookButton = styled.a`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  text-align: center;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  @media (max-width: 576px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
  
  svg {
    margin-right: 0.75rem;
    flex-shrink: 0;
  }
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(157, 109, 73, 0.3);
  }
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(16, 29, 64, 0.05);
  border-radius: 4px;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const DangerText = styled.span`
  color: #d32f2f;
  font-weight: 700;
`;

const MemberBadge = styled.span`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-right: 0;
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const TicketTierBadge = styled.div`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  display: inline-block;
  margin-top: 1rem;
`;

const SellingFastBadge = styled.div`
  background-color: rgba(255, 0, 0, 0.4);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  display: inline-block;
  margin-top: 1rem;
  margin-left: 1rem;
  border: 1px solid rgba(255, 0, 0, 0.7);
`;

const BackButton = styled.button`
  background: transparent;
  color: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  margin: 3rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  max-width: 200px;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
  }
`;

const EventSidebar = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    position: relative;
    padding-bottom: 0.5rem;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 2px;
      background-color: ${props => props.theme.colors.accent};
    }
  }
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  
  h4 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.1rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #333;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const EventTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.9rem;
  backdrop-filter: blur(4px);
  display: inline-block;
  margin-bottom: 0.3rem;
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 576px) {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const Tag = styled.span`
  background-color: ${props => props.theme.colors.accentLight};
  color: ${props => props.theme.colors.accent};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-block;
  margin-bottom: 0.3rem;
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 576px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const GallerySection = styled.section`
  width: 100%;
  max-width: 1600px;
  margin: 4rem auto;
  padding: 0 5rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 2rem;
    margin: 3rem auto;
  }
  
  @media (max-width: 576px) {
    padding: 0 1rem;
    margin: 2rem auto;
  }
`;

const GalleryMosaic = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 220px;
  gap: 1.5rem;
  margin-top: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-auto-rows: 180px;
  }
`;

const GalleryImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  &:nth-child(3n+1) {
    grid-column: span 1;
    grid-row: span 1;
  }
  
  &:nth-child(3n+2) {
    grid-column: span 1;
    grid-row: span 1;
  }
  
  &:nth-child(3n) {
    grid-column: span 1;
    grid-row: span 1;
  }
  
  &:first-child {
    grid-column: span 2;
    grid-row: span 2;
  }
  
  @media (max-width: 1200px) {
    &:first-child {
      grid-column: span 2;
      grid-row: span 1;
    }
  }
  
  @media (max-width: 768px) {
    &:first-child {
      grid-column: span 1;
      grid-row: span 1;
    }
  }
`;

const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '0.5rem' }}>
    <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
  </svg>
);

const ErrorMessage = ({ message, onBack }) => (
  <div style={{ 
    padding: '2rem', 
    textAlign: 'center', 
    color: '#721c24', 
    background: '#f8d7da', 
    borderRadius: '4px',
    margin: '2rem auto',
    maxWidth: '800px'
  }}>
    <h2>Error</h2>
    <p>{message}</p>
    <BackButton onClick={onBack}>
      <BackIcon /> Go Back
    </BackButton>
  </div>
);

const formatEventDate = (startDateStr, endDateStr) => {
  if (!startDateStr) return "Date TBD";
  
  const startDate = new Date(startDateStr);
  if (isNaN(startDate.getTime())) return "Date TBD";
  
  const startFormatted = startDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  if (!endDateStr) return startFormatted;
  
  const endDate = new Date(endDateStr);
  if (isNaN(endDate.getTime())) return startFormatted;
  
  const endFormatted = endDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  return `${startFormatted} - ${endFormatted}`;
};

const getDefaultImageForLocation = (location) => {
  switch(location) {
    case 'CASTLE': return castleImage;
    case 'PARKLAND': return gardensImage;
    case 'RETAIL VILLAGE': return retailImage;
    default: return playgroundImage;
  }
};

const GET_EVENT_QUERY = gql`
  query GetEvent($id: ID!, $idType: EventIdType) {
    event(id: $id, idType: $idType) {
      id
      slug
      uri
      title
      date
      featuredImage {
        node {
          sourceUrl
        }
      }
      eventSEO {
        seoTitle
        seoDescription
        seoKeywords
        seoImageUrl
        seoCanonical
        seoNoindex
        schemaOverrideJson
        schemaPerformers {
          name
          sameAs
        }
      }
      eventDetails {
        eventLocation
        eventDescription
        eventShortDescription
        eventDate
        eventEndDate
        eventTime
        eventTicketTier
        eventTags
        eventMemberIncluded
        eventGalleryEnabled
        eventGalleryImages {
          image1 {
            node {
              sourceUrl
            }
          }
          image2 {
            node {
              sourceUrl
            }
          }
          image3 {
            node {
              sourceUrl
            }
          }
          image4 {
            node {
              sourceUrl
            }
          }
          image5 {
            node {
              sourceUrl
            }
          }
          image6 {
            node {
              sourceUrl
            }
          }
        }
        eventTicketPrices {
          adult
          child
          concession
        }
        eventBookingUrl
        eventStrapline
        eventSection1Enabled
        eventSection1Title
        eventSection1Content
        eventSection1Image {
          node {
            sourceUrl
          }
        }
        eventSection2Enabled
        eventSection2Title
        eventSection2Content
        eventSection2Image {
          node {
            sourceUrl
          }
        }
      }
    }
  }
`;

const FeatureSection = styled.section`
  width: 100%;
  max-width: 1600px;
  margin: 4rem auto;
  padding: 0 5rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 2rem;
    margin: 3rem auto;
  }
  
  @media (max-width: 576px) {
    padding: 0 1rem;
    margin: 2rem auto;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.imageRight ? '1fr 1fr' : '1fr 1fr'};
  gap: 3rem;
  max-width: 1600px;
  margin: 0 auto;
  padding: 3rem 2rem;
  align-items: center;
  
  @media (max-width: 992px) {
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 2rem 1rem;
  }
  
  @media (max-width: 576px) {
    padding: 1.5rem 0;
  }
`;

const FeatureImage = styled.div`
  width: 100%;
  height: 450px;
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  order: ${props => props.imageRight ? 2 : 1};
  position: relative;
  overflow: hidden;
  transform: ${props => props.imageRight ? 'rotate(2deg)' : 'rotate(-2deg)'};
  transition: transform 0.5s ease;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.3) 100%);
    border-radius: 12px;
  }
  
  &:hover {
    transform: ${props => props.imageRight ? 'rotate(0deg)' : 'rotate(0deg)'} scale(1.02);
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
  
  @media (max-width: 768px) {
    height: 350px;
    order: 1;
    transform: rotate(0deg);
  }
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  order: ${props => props.imageRight ? 1 : 2};
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
  
  ${props => props.imageRight ? 
    'transform: translateX(3rem);' : 
    'transform: translateX(-3rem);'
  }
  
  @media (max-width: 768px) {
    order: 2;
    transform: translateX(0) translateY(-2rem);
    margin: 0 1rem;
  }
`;

const FeatureTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
  position: relative;
  padding-bottom: 0.75rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, ${props => props.theme.colors.accent}, ${props => props.theme.colors.accentLight});
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FeatureText = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.text};
  
  p {
    margin-bottom: 1.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
      text-decoration: underline;
    }
  }
`;

const BookNowButton = styled.a`
  display: block;
  width: calc(100% - 2rem);
  background: ${props => props.theme.colors.accent};
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-weight: 600;
  font-size: 1.3rem;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  margin: 1.5rem auto 0;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  box-sizing: border-box;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.theme.colors.accentDark || props.theme.colors.accent};
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 576px) {
    font-size: 1.2rem;
    padding: 0.7rem 1.2rem;
  }
`;

const GiftVoucherButton = styled.a`
  display: block;
  width: calc(100% - 2rem);
  background: ${props => props.theme.colors.primary};
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-weight: 600;
  font-size: 1.3rem;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  margin: 1rem auto 0;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  box-sizing: border-box;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark || props.theme.colors.primary};
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  @media (max-width: 576px) {
    font-size: 1.2rem;
    padding: 0.7rem 1.2rem;
  }
`;

// Define data for the three global slant sections
const getDineSlantData = () => {
  return {
    image: tapasImage,
    title: 'Fine Dining at Belvoir Castle',
    description: 'Experience exquisite cuisine in the stunning surroundings of Belvoir Castle. Our award-winning chefs create memorable dining experiences using the finest local ingredients.',
    buttonText: 'View Dining Options',
    buttonLink: '/castle-dining'
  };
};

const getShopSlantData = () => {
  return {
    image: retailImage,
    title: 'Shop at Belvoir Castle',
    description: 'Discover our unique range of gifts, souvenirs, and local produce at the Belvoir Castle shops. From luxury homeware to artisan food and drink, there\'s something for everyone.',
    buttonText: 'Explore Our Shops',
    buttonLink: '/retail-village'
  };
};

const getStaySlantData = () => {
  return {
    image: accommodationImage,
    title: 'Luxury Accommodation',
    description: 'Extend your visit with a luxurious stay at Belvoir Castle. Our elegant rooms and suites offer the perfect retreat after a day of exploration.',
    buttonText: 'Book Your Stay',
    buttonLink: '/stay-with-us'
  };
};

// ─── Solstice-specific styled components ────────────────────────────────────

const SolsticeEyebrow = styled.span`
  display: inline-block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.92);
  background: rgba(157, 109, 73, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.45rem 1.2rem;
  border-radius: 999px;
  margin-bottom: 1.25rem;
`;

const SolsticeHighlightsBar = styled.div`
  background: linear-gradient(135deg, #0c1730 0%, #101d40 55%, #1a2b48 100%);
  padding: 2.25rem 5rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid rgba(157, 109, 73, 0.2);
  border-bottom: 1px solid rgba(157, 109, 73, 0.2);

  @media (max-width: 1024px) {
    padding: 2rem 2rem;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem 0;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr 1fr;
    padding: 1.75rem 1.25rem;
    gap: 1.5rem 0;
  }
`;

const SolsticeHighlight = styled.div`
  text-align: center;
  padding: 0 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 1024px) {
    border-right: none;
    padding: 0.75rem 0.5rem;
  }

  .hl-icon-wrap {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    background: rgba(157, 109, 73, 0.18);
    border: 1px solid rgba(157, 109, 73, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.9rem;
    color: #c4956a;
    flex-shrink: 0;
  }

  .hl-label {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.08rem;
    font-weight: 700;
    color: #fff;
    display: block;
    margin-bottom: 0.3rem;
    line-height: 1.25;
  }

  .hl-value {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.5);
    letter-spacing: 0.03em;
    line-height: 1.5;
  }
`;

const SolsticePremiumInfoSection = styled.div`
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid rgba(157, 109, 73, 0.18);
  box-shadow: 0 12px 36px rgba(16, 29, 64, 0.09);
  margin-bottom: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 140px;

  @media (max-width: 1024px) {
    position: static;
    padding: 1.5rem;
  }

  @media (max-width: 576px) {
    padding: 1.25rem;
  }
`;

const SolsticeSectionDivider = styled.div`
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #9d6d49, rgba(157, 109, 73, 0.3));
  border-radius: 2px;
  margin-bottom: 1.5rem;
`;

const SolsticeBookButton = styled.a`
  display: block;
  width: 100%;
  background: linear-gradient(135deg, #9d6d49 0%, #7a5238 100%);
  color: white;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 700;
  font-size: 1.25rem;
  padding: 0.9rem 1.5rem;
  border-radius: 12px;
  text-decoration: none;
  margin-top: 1.5rem;
  text-align: center;
  box-shadow: 0 8px 24px rgba(157, 109, 73, 0.35);
  transition: all 0.25s ease;
  box-sizing: border-box;
  letter-spacing: 0.02em;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(157, 109, 73, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 576px) {
    font-size: 1.1rem;
    padding: 0.8rem 1.2rem;
  }
`;

const SolsticeInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.7rem 0;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);

  &:last-of-type {
    border-bottom: none;
  }

  .row-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: rgba(157, 109, 73, 0.08);
    border: 1px solid rgba(157, 109, 73, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9d6d49;
    flex-shrink: 0;
  }

  .row-content {
    flex: 1;

    .row-label {
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(16, 29, 64, 0.4);
      margin-bottom: 0.15rem;
    }

    .row-value {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1rem;
      font-weight: 600;
      color: #101d40;
      line-height: 1.3;
    }
  }
`;

const SolsticeBookingBanner = styled.div`
  max-width: 1600px;
  margin: 2rem auto 0;
  padding: 0 5rem;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 0 2rem;
  }

  @media (max-width: 576px) {
    padding: 0 1rem;
  }
`;

const SolsticeBookingBannerInner = styled.div`
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(157, 109, 73, 0.18);
  box-shadow: 0 12px 36px rgba(16, 29, 64, 0.08);
  border-radius: 16px;
  padding: 1.75rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 0;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SolsticeBookingBannerCell = styled.div`
  padding: 0 1.5rem;
  border-right: 1px solid rgba(16, 29, 64, 0.1);

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: none;
    padding-right: 0;
  }

  @media (max-width: 1024px) {
    border-right: none;
    padding: 0;
  }

  .bc-label {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(16, 29, 64, 0.4);
    margin-bottom: 0.25rem;
  }

  .bc-value {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: #101d40;
    line-height: 1.3;
  }

  .bc-sub {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.78rem;
    color: rgba(16, 29, 64, 0.5);
    margin-top: 0.1rem;
  }
`;

const SolsticeFullWidthContent = styled.div`
  max-width: 1600px;
  margin: 3rem auto 0;
  padding: 0 5rem;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 0 2rem;
    margin-top: 2rem;
  }

  @media (max-width: 576px) {
    padding: 0 1rem;
    margin-top: 1.5rem;
  }
`;

const SolsticeRichContent = styled.div`
  font-size: 1.05rem;
  line-height: 1.85;
  color: rgba(16, 29, 64, 0.72);
  width: 100%;
  box-sizing: border-box;

  p {
    margin-bottom: 1.25rem;
    max-width: 72ch;
  }

  hr {
    border: none;
    border-top: 1px solid rgba(157, 109, 73, 0.2);
    margin: 2.5rem 0;
  }

  h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.6rem, 3vw, 2.1rem);
    font-weight: 700;
    color: #101d40;
    margin: 3rem 0 1.25rem;
    padding-top: 2.5rem;
    border-top: 1px solid rgba(157, 109, 73, 0.18);
    line-height: 1.2;

    &:first-of-type {
      margin-top: 0;
      padding-top: 0;
      border-top: none;
    }
  }

  h3 {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.2rem, 2.2vw, 1.5rem);
    font-weight: 700;
    color: #9d6d49;
    margin: 2rem 0 0.5rem;
    line-height: 1.3;
  }

  h4 {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(16, 29, 64, 0.5);
    margin: 1.5rem 0 0.5rem;
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin-bottom: 1.5rem;
    max-width: 60ch;

    li {
      padding: 0.3rem 0 0.3rem 1.5rem;
      position: relative;
      color: rgba(16, 29, 64, 0.7);

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #9d6d49;
        opacity: 0.7;
      }
    }
  }

  ol {
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;
  }

  strong {
    font-weight: 700;
    color: #101d40;
  }

  a {
    color: #9d6d49;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }

  * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const SOLSTICE_GALLERY_IMAGES = {
  garden: 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/ellie-2.webp',
  hosts: 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/rina-and-duchess-2-scaled.webp',
  tea: 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/06/P1406207.01_12_32_03.Still002-scaled.webp',
};

const SolsticeGallery = styled.section`
  margin: clamp(2.5rem, 5vw, 3.5rem) 0;
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 5vw, 4rem);
`;

const SolsticeGalleryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.5rem, 4vw, 3rem);
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SolsticeGalleryImageWrap = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.9) inset,
    0 20px 48px rgba(16, 29, 64, 0.1),
    0 0 0 1px rgba(157, 109, 73, 0.1);
  order: ${props => (props.$reverse ? 2 : 0)};

  @media (max-width: 768px) {
    order: 0;
    border-radius: 12px;
  }
`;

const SolsticeGalleryImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: ${props => (props.$tall ? '3 / 4' : '4 / 3')};
  object-fit: cover;
  object-position: ${props => props.$pos || 'center center'};

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
  }
`;

const SolsticeGalleryCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  order: ${props => (props.$reverse ? 1 : 0)};

  @media (max-width: 768px) {
    order: 1;
  }
`;

const SolsticeGalleryEyebrow = styled.span`
  display: inline-block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #9d6d49;
  background: rgba(157, 109, 73, 0.08);
  border: 1px solid rgba(157, 109, 73, 0.2);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  align-self: flex-start;
`;

const SolsticeGalleryTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 700;
  color: #101d40;
  margin: 0;
  line-height: 1.2;
`;

const SolsticeGalleryBody = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.92rem, 1.6vw, 1rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.62);
  margin: 0;
  max-width: 42ch;
`;

const SolsticeHostsFeature = styled.figure`
  margin: 0;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.9) inset,
    0 24px 56px rgba(16, 29, 64, 0.12),
    0 0 0 1px rgba(157, 109, 73, 0.12);
`;

const SolsticeHostsImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 21 / 9;
  object-fit: cover;
  object-position: center 40%;

  @media (max-width: 768px) {
    aspect-ratio: 16 / 10;
  }
`;

const SolsticeHostsCaption = styled.figcaption`
  background: linear-gradient(
    135deg,
    #101d40 0%,
    #1a2b48 100%
  );
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2.5rem);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SolsticeHostsCaptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const SolsticeHostsCaptionEyebrow = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(196, 149, 106, 0.95);
`;

const SolsticeHostsCaptionTitle = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.25rem, 2.5vw, 1.65rem);
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.25;
`;

const SolsticeHostsCaptionSub = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
  line-height: 1.5;
`;

const SolsticeProse = styled.div`
  max-width: 72ch;
  margin-bottom: clamp(2rem, 4vw, 3rem);
`;

const SolsticeLead = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.2rem, 2.2vw, 1.45rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.82);
  margin: 0 0 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SolsticeParagraph = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.95rem, 1.6vw, 1.05rem);
  line-height: 1.75;
  color: rgba(16, 29, 64, 0.68);
  margin: 0 0 1.15rem;

  strong {
    font-weight: 700;
    color: #101d40;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SolsticeSectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.65rem, 3.2vw, 2.25rem);
  font-weight: 700;
  color: #101d40;
  margin: 0 0 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const SolsticeSubsectionTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: #101d40;
  margin: 0 0 1.5rem;
  padding-top: 0.5rem;
  line-height: 1.25;
`;

const SolsticeDivider = styled.hr`
  border: none;
  border-top: 1px solid rgba(157, 109, 73, 0.18);
  margin: clamp(2.5rem, 5vw, 3.5rem) 0;
`;

const SolsticeScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid rgba(157, 109, 73, 0.15);
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(
    180deg,
    rgba(250, 247, 242, 0.6) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
`;

const SolsticeScheduleItem = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 1.25rem;
  padding: 1.15rem 1.5rem;
  border-bottom: 1px solid rgba(157, 109, 73, 0.1);
  align-items: start;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    padding: 1rem 1.15rem;
  }
`;

const SolsticeScheduleTime = styled.span`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #9d6d49;
  white-space: nowrap;
  padding-top: 0.15rem;
`;

const SolsticeScheduleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const SolsticeScheduleItemTitle = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #101d40;
  margin: 0;
  line-height: 1.3;
`;

const SolsticeScheduleItemDesc = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.92rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.58);
  margin: 0;
`;

const SolsticeBringSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const SolsticeBringList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  max-width: 36ch;
  text-align: center;

  li {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.95rem;
    line-height: 1.55;
    color: rgba(16, 29, 64, 0.68);
    padding-left: 0;
    position: relative;

    &::before {
      content: '·';
      margin-right: 0.45rem;
      color: #9d6d49;
      font-weight: 700;
    }
  }
`;

const SOLSTICE_SCHEDULE = [
  {
    time: '8:30am',
    title: 'Arrival and Welcome',
    desc: 'Arrive at the Stone Circle and settle into the morning as we gather together in community.',
  },
  {
    time: '9:00am',
    title: 'Opening Ritual',
    desc: 'We will open the morning by setting our intention for the Solstice and honouring this seasonal turning point.',
  },
  {
    time: '9:30am',
    title: 'Meditation to Welcome the Light',
    desc: 'A guided meditation led by Sue, inviting stillness, reflection and a gentle welcome to the light of midsummer.',
  },
  {
    time: '10:00am',
    title: 'Drumming and Calling in Joy',
    desc: 'A joyful celebration of life, gratitude and the fullness of summer through rhythm, sound and shared energy.',
  },
  {
    time: '10:30am',
    title: 'Singing, Dancing and Community Celebration',
    desc: 'A time for singing, movement, laughter and celebration as we honour the longest day of the year together.',
  },
  {
    time: '11:30am',
    title: 'Closing Ritual',
    desc: 'We will close the gathering by giving thanks to the land, to one another, and to the season ahead.',
  },
  {
    time: '12:00pm',
    title: 'Tea at Belvoir Lodge',
    desc: 'Join us at Belvoir Lodge for tea, conversation and informal community connection.',
  },
  {
    time: '1:00pm',
    title: 'Closing and Departure',
    desc: 'The morning comes to a close with gratitude and a shared welcome to summer.',
  },
];

const SOLSTICE_BRING_ITEMS = [
  'A musical instrument, if you have one',
  'A song, poem, prayer or blessing to share',
  'An offering for the land',
  'Comfortable clothing suitable for the weather',
  'A blanket or cushion, if you wish',
  'An open heart and a spirit of celebration',
];

// ─── End Solstice-specific components ────────────────────────────────────────

const TimelineSection = styled.section`
  width: 100%;
  max-width: 1600px;
  margin: 4rem auto;
  padding: 0 5rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 2rem;
    margin: 3rem auto;
  }
  
  @media (max-width: 576px) {
    padding: 0 1rem;
    margin: 2rem auto;
  }
`;

const TimelineDaysContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TimelineDaySection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const TimelineContainer = styled.div`
  position: relative;
  margin: 0 auto;
  
  &:before {
    content: '';
    position: absolute;
    width: 2px;
    background: ${props => props.theme.colors.accent};
    top: 0;
    bottom: 0;
    left: 30px;
  }
`;

const TimelineItem = styled.div`
  padding: 10px 40px;
  position: relative;
  box-sizing: border-box;
  animation: fadeIn 0.5s ease-in-out;
  animation-fill-mode: both;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    background: ${props => props.theme.colors.accent};
    border-radius: 50%;
    top: 15px;
    left: -10px;
    box-shadow: 0 0 0 4px ${props => props.theme.colors.accentLight};
  }
`;

const TimelineTime = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const TimelineContent = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    border-style: solid;
    border-width: 10px;
    top: 15px;
    
    ${props => props.isEven ? `
      border-color: transparent transparent transparent white;
      right: 100%;
    ` : `
      border-color: transparent white transparent transparent;
      left: 100%;
    `}
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const TimelineTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const TimelineDescription = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0;
`;

const EventDetail = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  
  let routeIdentifier = id || null;
  let idType = 'ID';
  if (slug) {
    const normalizedSlug = String(slug).trim().replace(/^\/+|\/+$/g, '').toLowerCase();
    routeIdentifier = normalizedSlug;
    idType = 'SLUG';
  } else if (id) {
    const raw = String(id).trim();
    if (raw.includes('/')) {
      const normalized = raw.replace(/^\/+|\/+$/g, '').toLowerCase();
      routeIdentifier = `/events/${normalized}/`;
      idType = 'URI';
    } else if (raw.includes('-')) {
      routeIdentifier = raw.toLowerCase();
      idType = 'SLUG';
    }
  }

  // Use the What's On section hook
  useWhatsOnSection();
  
  const { loading, error, data } = useQuery(GET_EVENT_QUERY, {
    variables: { id: routeIdentifier, idType },
    skip: !routeIdentifier,
    onError: (error) => {


    },
    onCompleted: (data) => {

      if (!data || !data.event) {

      }
    }
  });

  const SOLSTICE_EVENT_ID = 'cG9zdDo1MzI=';
  const isSolsticeEvent = data?.event?.id === SOLSTICE_EVENT_ID;

  // Mobile solstice: match padding-top to the live fixed header (nav + banners + safari tag)
  useEffect(() => {
    if (!isSolsticeEvent) return undefined;

    const syncHeaderOffset = () => {
      const header = document.getElementById('site-header');
      if (!header) return;
      document.documentElement.style.setProperty(
        '--site-header-height',
        `${header.offsetHeight}px`
      );
    };

    syncHeaderOffset();
    const raf = requestAnimationFrame(syncHeaderOffset);
    const t1 = window.setTimeout(syncHeaderOffset, 150);
    const t2 = window.setTimeout(syncHeaderOffset, 2600);

    window.addEventListener('resize', syncHeaderOffset);
    window.addEventListener('orientationchange', syncHeaderOffset);

    const header = document.getElementById('site-header');
    const observer =
      header &&
      new MutationObserver(syncHeaderOffset);
    if (header && observer) {
      observer.observe(header, { childList: true, subtree: true, attributes: true });
    }

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', syncHeaderOffset);
      window.removeEventListener('orientationchange', syncHeaderOffset);
      observer?.disconnect();
      document.documentElement.style.removeProperty('--site-header-height');
    };
  }, [isSolsticeEvent]);
  
  if (!routeIdentifier) {

    return <ErrorMessage message="No event ID provided" onBack={() => navigate('/events')} />;
  }
  
  if (loading) {

    return <LoadingSpinner />;
  }
  
  if (error) {

    return <ErrorMessage message={`Error: ${error.message}`} onBack={() => navigate('/events')} />;
  }
  
  if (!data || !data.event) {

    return <ErrorMessage message="Event not found" onBack={() => navigate('/events')} />;
  }
  
  const eventData = data.event;
  const details = eventData.eventDetails || {};
  const seo = eventData.eventSEO || {};
  const isSolstice = eventData.id === SOLSTICE_EVENT_ID;

  // Get hero image - force specific images for certain events
  const heroImage = (data?.event?.id === 'cG9zdDoxNTI=') 
    ? christmasImage 
    : (data?.event?.id === 'cG9zdDoyNjA=') 
    ? bannerImage
    : (eventData.featuredImage?.node?.sourceUrl || getDefaultImageForLocation(details.eventLocation));

  // Format date
  const formattedDate = formatEventDate(details.eventDate, details.eventEndDate);

  // SEO helpers
  const metaTitle = seo.seoTitle || `${eventData.title} | Events at Belvoir Castle`;
  const metaDescription = seo.seoDescription || details.eventShortDescription || (details.eventStrapline || '').toString().slice(0, 155);
  const siteBase = 'https://www.belvoircastle.com';
  const normalizeCanonical = (value) => {
    if (!value || typeof value !== 'string') return null;
    let v = value.trim();
    if (!v) return null;
    if (v.startsWith('@')) v = v.slice(1).trim();
    if (!v) return null;
    // Absolute URL
    if (/^https?:\/\//i.test(v)) {
      try { new URL(v); return v; } catch { return null; }
    }
    // www.* → absolute
    if (/^www\./i.test(v)) {
      const abs = `https://${v}`;
      try { new URL(abs); return abs; } catch { return null; }
    }
    // Relative path or bare slug → ensure under /events/{slug}
    const toEventsUrl = (pathOrSlug) => {
      const cleaned = String(pathOrSlug).trim().replace(/^\/+|\/+$/g, '');
      const lower = cleaned.toLowerCase();
      const path = lower.startsWith('events/') ? `/${lower}` : `/events/${lower}`;
      return `${siteBase}${path}`;
    };
    if (v.startsWith('/')) {
      return toEventsUrl(v);
    }
    // Bare path or slug
    return toEventsUrl(v);
  };
  // Debug logs for SEO canonical normalization

  const canonicalUrl = normalizeCanonical(seo.seoCanonical)
    || (eventData.uri ? `${siteBase}${eventData.uri}` : (typeof window !== 'undefined' ? window.location.href : `${siteBase}/events/${eventData.slug || eventData.id}`));

  const metaImage = seo.seoImageUrl || eventData.featuredImage?.node?.sourceUrl || heroImage;
  const metaKeywords = seo.seoKeywords || (details.eventTags ? [].concat(details.eventTags).join(', ') : undefined);

  // Compute price range for schema.org
  const prices = [
    details?.eventTicketPrices?.adult,
    details?.eventTicketPrices?.child,
    details?.eventTicketPrices?.concession
  ]
    .map(v => (typeof v === 'string' ? parseFloat(v) : v))
    .filter(v => typeof v === 'number' && !isNaN(v));
  const lowPrice = prices.length ? Math.min(...prices) : undefined;
  const highPrice = prices.length ? Math.max(...prices) : undefined;

  // Build Event JSON-LD
  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventData.title,
    description: metaDescription,
    image: [heroImage].filter(Boolean),
    startDate: details.eventDate || undefined,
    endDate: details.eventEndDate || details.eventDate || undefined,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: details.eventLocation || 'Belvoir Castle',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Castle',
        addressLocality: 'Grantham',
        postalCode: 'NG32 1PA',
        addressCountry: 'GB'
      }
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com'
    },
    performer: Array.isArray(seo?.schemaPerformers) && seo.schemaPerformers.length > 0
      ? seo.schemaPerformers.map(p => ({
          '@type': 'Person',
          name: p?.name,
          sameAs: p?.sameAs || undefined
        }))
      : undefined,
    offers: details.eventBookingUrl && (lowPrice || highPrice)
      ? {
          '@type': 'AggregateOffer',
          url: details.eventBookingUrl,
          priceCurrency: 'GBP',
          lowPrice: lowPrice,
          highPrice: highPrice,
          availability: 'https://schema.org/InStock'
        }
      : undefined,
    url: canonicalUrl
  };
  const schemaScript = seo?.schemaOverrideJson && typeof seo.schemaOverrideJson === 'string' && seo.schemaOverrideJson.trim().startsWith('{')
    ? seo.schemaOverrideJson
    : JSON.stringify(eventSchema);
  
  // Expanded debug: prove sources from WordPress vs fallbacks and what is used
  try {
    const metaTitleSource = seo?.seoTitle ? 'wordpress:eventSEO.seoTitle' : 'fallback:Event title + suffix';
    const metaDescriptionSource = seo?.seoDescription
      ? 'wordpress:eventSEO.seoDescription'
      : (details?.eventShortDescription
          ? 'fallback:eventDetails.eventShortDescription'
          : (details?.eventStrapline ? 'fallback:eventDetails.eventStrapline' : 'fallback:none'));
    const canonicalSource = seo?.seoCanonical ? 'wordpress:eventSEO.seoCanonical (normalized)' : (eventData?.uri ? 'fallback:event.uri' : 'fallback:window.location or slug');
    const imageSource = (seo?.seoImageUrl
      ? 'wordpress:eventSEO.seoImageUrl'
      : (eventData?.featuredImage?.node?.sourceUrl ? 'fallback:event.featuredImage' : 'fallback:heroImage'));
    
    console.groupCollapsed('[EventDetail SEO] Proof of WP SEO fields and computed usage');


    console.groupEnd();
  } catch (e) {

  }
  
  // JSON-LD override vs generated
  try {
    const usingOverride = typeof seo?.schemaOverrideJson === 'string' && seo.schemaOverrideJson.trim().startsWith('{');
    console.groupCollapsed('[EventDetail SEO] JSON-LD selection');

    if (usingOverride) {
      try {

      } catch (e) {
        

      }
    } else {

    }
    console.groupEnd();
  } catch (e) {

  }
  
  const getEventTags = () => {
    if (!details.eventTags) {

      return null;
    }
    
    const tags = Array.isArray(details.eventTags) ? details.eventTags : [details.eventTags];

    return tags;
  };
  
  const getTicketTierName = (tier) => {
    const tierNames = {
      'super_early_bird': 'Super Early Bird',
      'early_bird': 'Early Bird',
      'advanced': 'Advanced',
      'standard': 'Standard'
    };
    
    return tierNames[tier] || tier;
  };
  
  // Add icons for the component
  const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
    </svg>
  );

  const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
      <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
    </svg>
  );

  const LocationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
    </svg>
  );

  const TicketIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5V6a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 11.5V10a.5.5 0 0 1 .5-.5 1.5 1.5 0 1 0 0-3A.5.5 0 0 1 0 6V4.5ZM1.5 4a.5.5 0 0 0-.5.5v1.05a2.5 2.5 0 0 1 0 4.9v1.05a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-1.05a2.5 2.5 0 0 1 0-4.9V4.5a.5.5 0 0 0-.5-.5h-13Z"/>
    </svg>
  );

  const MemberIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm-9 8c0 1 1 1 1 1h5.256A4.493 4.493 0 0 1 8 12.5a4.49 4.49 0 0 1 1.544-3.393C9.077 9.038 8.564 9 8 9c-5 0-6 3-6 4Zm9.886-3.54c.18-.613 1.048-.613 1.229 0l.043.148a.64.64 0 0 0 .921.382l.136-.074c.561-.306 1.175.308.87.869l-.075.136a.64.64 0 0 0 .382.92l.149.045c.612.18.612 1.048 0 1.229l-.15.043a.64.64 0 0 0-.38.921l.074.136c.305.561-.309 1.175-.87.87l-.136-.075a.64.64 0 0 0-.92.382l-.045.149c-.18.612-1.048.612-1.229 0l-.043-.15a.64.64 0 0 0-.921-.38l-.136.074c-.561.305-1.175-.309-.87-.87l.075-.136a.64.64 0 0 0-.382-.92l-.148-.045c-.613-.18-.613-1.048 0-1.229l.148-.043a.64.64 0 0 0 .382-.921l-.074-.136c-.306-.561.308-1.175.869-.87l.136.075a.64.64 0 0 0 .92-.382l.045-.148ZM14 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z"/>
    </svg>
  );

  const BookingIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M0 2.5A1.5 1.5 0 0 1 1.5 1h11A1.5 1.5 0 0 1 14 2.5v10.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 0 13V2.5zM1.5 2a.5.5 0 0 0-.5.5v10.5a.5.5 0 0 0 .5.5h11a.5.5 0 0 0 .5-.5V2.5a.5.5 0 0 0-.5-.5h-11z"/>
      <path d="M3.5 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0-2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm0 5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5z"/>
    </svg>
  );

  const TagIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M3 2v4.586l7 7L14.586 9l-7-7H3zM2 2a1 1 0 0 1 1-1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 2 6.586V2z"/>
      <path d="M5.5 5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm0 1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
    </svg>
  );

  const renderTimeline = () => {
    // 1940s Afternoon Tea Festival timeline (event id: cG9zdDoxNjc=)
    if ((data?.event?.id || id) !== 'cG9zdDoxNjc=') return null;

    const timelineData = [
      { time: '09:30', title: 'DJ Dr Swing' },
      { time: '10:30', title: 'Johnny Victory' },
      { time: '11:15', title: 'DJ Dr Swing' },
      { time: '11:45', title: 'Blue Bird Belles' },
      { time: '12:30', title: 'DJ Dr Swing' },
      { time: '13:00', title: 'Lili Redman' },
      { time: '13:45', title: 'DJ Dr Swing' },
      { time: '14:15', title: 'Johnny Victory' },
      { time: '15:00', title: 'Blue Bird Belles' },
      { time: '15:45', title: 'DJ Dr Swing' },
    ];

    return (
      <TimelineSection>
        <SectionTitle>Event Schedule</SectionTitle>
        <TimelineContainer>
          {timelineData.map((item, index) => (
            <TimelineItem key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              <TimelineTime>{item.time}</TimelineTime>
              <TimelineContent>
                <TimelineTitle>{item.title}</TimelineTitle>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </TimelineSection>
    );
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription || 'Join us for this special event at Belvoir Castle'} />
        {metaKeywords && <meta name="keywords" content={`Belvoir Castle events, ${eventData.title}, castle events, ${metaKeywords}`} />}
        <link rel="canonical" href={canonicalUrl} />
        {seo?.seoNoindex && <meta name="robots" content="noindex,nofollow" />}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
        <script type="application/ld+json">{schemaScript}</script>
      </Helmet>
      <EventContainer $isSolstice={isSolstice}>
        <EventHero 
          image={heroImage} 
          $isSolstice={isSolstice}
          $backgroundPosition={eventData.id === 'cG9zdDo0Mzk=' ? 'center 25%' : undefined}
        >
          {isSolstice ? (
          <EventHeroContentSolstice>
            <SolsticeEyebrow>Belvoir Castle Estate · Sunday 21 June 2026</SolsticeEyebrow>
            <EventTitle style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', marginBottom: '0.75rem' }}>
              {eventData.title}
            </EventTitle>
            <EventDate style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.8)', marginBottom: '1.25rem' }}>
              Sunday 21 June · Gather at the Stone Circle for the longest day of the year
            </EventDate>
            <EventLocation>{details.eventLocation}</EventLocation>
          </EventHeroContentSolstice>
          ) : (
          <EventHeroContent>
            <EventTitle>{eventData.title}</EventTitle>
            <EventDate>{formattedDate}{details.eventTime ? ` at ${details.eventTime}` : ''}</EventDate>
            <EventLocation>{details.eventLocation}</EventLocation>
            {details.eventTicketTier && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TicketTierBadge tier={details.eventTicketTier}>
                  {getTicketTierName(details.eventTicketTier)}
                </TicketTierBadge>
                {(eventData.id === 'cG9zdDo0MA==') && (
                  <SellingFastBadge>
                    Selling Fast
                  </SellingFastBadge>
                )}
              </div>
            )}
          </EventHeroContent>
          )}
        </EventHero>

        {isSolstice && (
          <SolsticeHighlightsBar>
            <SolsticeHighlight>
              <div className="hl-icon-wrap">
                {/* Sun / solstice */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2" x2="12" y2="5"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                  <line x1="2" y1="12" x2="5" y2="12"/>
                  <line x1="19" y1="12" x2="22" y2="12"/>
                  <line x1="4.93" y1="4.93" x2="7.05" y2="7.05"/>
                  <line x1="16.95" y1="16.95" x2="19.07" y2="19.07"/>
                  <line x1="4.93" y1="19.07" x2="7.05" y2="16.95"/>
                  <line x1="16.95" y1="7.05" x2="19.07" y2="4.93"/>
                </svg>
              </div>
              <span className="hl-label">Stone Circle</span>
              <span className="hl-value">Sun 21 June · Gather at 8:30am</span>
            </SolsticeHighlight>
            <SolsticeHighlight>
              <div className="hl-icon-wrap">
                {/* Music / sound waves */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </div>
              <span className="hl-label">Drumming &amp; Singing</span>
              <span className="hl-value">Ritual · Movement · Meditation</span>
            </SolsticeHighlight>
            <SolsticeHighlight>
              <div className="hl-icon-wrap">
                {/* Tea / cup */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 8h1a4 4 0 1 1 0 8h-1"/>
                  <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/>
                  <line x1="6" y1="2" x2="6" y2="4"/>
                  <line x1="10" y1="2" x2="10" y2="4"/>
                  <line x1="14" y1="2" x2="14" y2="4"/>
                </svg>
              </div>
              <span className="hl-label">Tea at Belvoir Lodge</span>
              <span className="hl-value">From 12:00pm · Closes 1:00pm</span>
            </SolsticeHighlight>
            <SolsticeHighlight>
              <div className="hl-icon-wrap">
                {/* People / community */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <span className="hl-label">All Are Welcome</span>
              <span className="hl-value">Come as you are · Free to attend</span>
            </SolsticeHighlight>
          </SolsticeHighlightsBar>
        )}

        {isSolstice ? (
          <>
            {/* Solstice: horizontal booking banner */}
            <SolsticeBookingBanner>
              <SolsticeBookingBannerInner>
                <SolsticeBookingBannerCell>
                  <div className="bc-label">Date</div>
                  <div className="bc-value">Sunday 21 June 2026</div>
                  <div className="bc-sub">Summer Solstice · Stone Circle</div>
                </SolsticeBookingBannerCell>
                <SolsticeBookingBannerCell>
                  <div className="bc-label">Stone Circle Gathering</div>
                  <div className="bc-value">8:30am – 11:30am</div>
                  <div className="bc-sub">Meditation · Ritual · Drumming · Singing</div>
                </SolsticeBookingBannerCell>
                <SolsticeBookingBannerCell>
                  <div className="bc-label">Tea at Belvoir Lodge</div>
                  <div className="bc-value">12:00pm – 1:00pm</div>
                  <div className="bc-sub">Informal conversation · All are welcome</div>
                </SolsticeBookingBannerCell>
                <SolsticeBookingBannerCell>
                  {details.eventBookingUrl && (
                    <SolsticeBookButton
                      href={details.eventBookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginTop: 0 }}
                    >
                      Book Your Place →
                    </SolsticeBookButton>
                  )}
                  <p style={{
                    fontFamily: 'system-ui, sans-serif',
                    fontSize: '0.72rem',
                    color: 'rgba(16, 29, 64, 0.4)',
                    textAlign: 'center',
                    margin: '0.6rem 0 0',
                  }}>Come as you are · Free to attend</p>
                </SolsticeBookingBannerCell>
              </SolsticeBookingBannerInner>
            </SolsticeBookingBanner>

            {/* Solstice: hardcoded content */}
            <SolsticeFullWidthContent>
              <SolsticeProse>
                <SolsticeLead>
                  Join us at the Stone Circle to mark the longest day of the year.
                </SolsticeLead>
                <SolsticeParagraph>
                  We&rsquo;ll gather at 8:30am for a morning of meditation, reflection, ritual,
                  drumming, singing, movement and community celebration — held in one of the most
                  ancient and atmospheric corners of the Belvoir Estate.
                </SolsticeParagraph>
                <SolsticeParagraph>
                  At 12:00pm, we&rsquo;ll make our way to Belvoir Lodge for tea and informal
                  conversation, where the morning continues in a gentler key. We&rsquo;ll finish
                  around 1:00pm.
                </SolsticeParagraph>
                <SolsticeParagraph>
                  <strong>All are welcome. Come as you are.</strong>
                </SolsticeParagraph>
              </SolsticeProse>

              <SolsticeGallery aria-label="Summer Solstice gathering">
                <SolsticeGalleryRow>
                  <SolsticeGalleryImageWrap>
                    <SolsticeGalleryImage
                      src={SOLSTICE_GALLERY_IMAGES.garden}
                      alt="A peaceful walk through the Belvoir Castle gardens on a summer morning"
                      loading="lazy"
                      $pos="center top"
                    />
                  </SolsticeGalleryImageWrap>
                  <SolsticeGalleryCopy>
                    <SolsticeGalleryEyebrow>Summer Solstice · Belvoir Estate</SolsticeGalleryEyebrow>
                    <SolsticeGalleryTitle>
                      A Celebration of Light, Community and the Turning of the Season
                    </SolsticeGalleryTitle>
                    <SolsticeGalleryBody>
                      Join the Duchess of Rutland at the Stone Circle for a soulful Summer Solstice
                      gathering to honour the longest day of the year.
                    </SolsticeGalleryBody>
                    <SolsticeGalleryBody style={{ marginTop: '0.75rem' }}>
                      The Summer Solstice has long been a time to pause, give thanks and celebrate
                      the fullness of summer. Step into the landscape of Belvoir, gather in community,
                      and welcome the light through meditation, ritual, music, movement and shared
                      celebration.
                    </SolsticeGalleryBody>
                  </SolsticeGalleryCopy>
                </SolsticeGalleryRow>

                <SolsticeHostsFeature>
                  <SolsticeHostsImage
                    src={SOLSTICE_GALLERY_IMAGES.hosts}
                    alt="Her Grace, the Duchess of Rutland, with Rina Golan in the Belvoir Castle gardens"
                    loading="lazy"
                  />
                  <SolsticeHostsCaption>
                    <SolsticeHostsCaptionText>
                      <SolsticeHostsCaptionEyebrow>Your hosts</SolsticeHostsCaptionEyebrow>
                      <SolsticeHostsCaptionTitle>
                        Her Grace, the Duchess of Rutland &amp; Rina Golan
                      </SolsticeHostsCaptionTitle>
                      <SolsticeHostsCaptionSub>
                        Held in one of the estate&rsquo;s most atmospheric outdoor spaces — a chance
                        to reconnect with the land, the season and one another.
                      </SolsticeHostsCaptionSub>
                    </SolsticeHostsCaptionText>
                  </SolsticeHostsCaption>
                </SolsticeHostsFeature>
              </SolsticeGallery>

              <SolsticeDivider />

              <SolsticeSubsectionTitle>Schedule</SolsticeSubsectionTitle>
              <SolsticeScheduleList>
                {SOLSTICE_SCHEDULE.map((item) => (
                  <SolsticeScheduleItem key={item.time + item.title}>
                    <SolsticeScheduleTime>{item.time}</SolsticeScheduleTime>
                    <SolsticeScheduleBody>
                      <SolsticeScheduleItemTitle>{item.title}</SolsticeScheduleItemTitle>
                      <SolsticeScheduleItemDesc>{item.desc}</SolsticeScheduleItemDesc>
                    </SolsticeScheduleBody>
                  </SolsticeScheduleItem>
                ))}
              </SolsticeScheduleList>

              <SolsticeGallery aria-label="Tea at Belvoir Lodge" style={{ marginTop: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                <SolsticeGalleryRow>
                  <SolsticeGalleryCopy $reverse>
                    <SolsticeGalleryEyebrow>Belvoir Lodge · 12:00pm</SolsticeGalleryEyebrow>
                    <SolsticeGalleryTitle>Tea and conversation</SolsticeGalleryTitle>
                    <SolsticeGalleryBody>
                      After the Stone Circle gathering, we make our way to Belvoir Lodge for tea,
                      conversation and informal community connection — a gentle close to the morning.
                    </SolsticeGalleryBody>
                  </SolsticeGalleryCopy>
                  <SolsticeGalleryImageWrap $reverse>
                    <SolsticeGalleryImage
                      src={SOLSTICE_GALLERY_IMAGES.tea}
                      alt="Tea being poured into fine china at Belvoir Lodge"
                      loading="lazy"
                      $pos="center"
                    />
                  </SolsticeGalleryImageWrap>
                </SolsticeGalleryRow>
              </SolsticeGallery>

              <SolsticeDivider />

              <SolsticeBringSection>
                <SolsticeSubsectionTitle>What to Bring</SolsticeSubsectionTitle>
                <SolsticeParagraph style={{ marginBottom: '1.25rem' }}>Please bring:</SolsticeParagraph>
                <SolsticeBringList>
                  {SOLSTICE_BRING_ITEMS.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </SolsticeBringList>
              </SolsticeBringSection>

              {details.eventBookingUrl && (
                <div style={{
                  marginTop: '3.5rem',
                  paddingTop: '2.5rem',
                  borderTop: '1px solid rgba(157, 109, 73, 0.18)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem',
                }}>
                  <SolsticeSectionTitle style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                    Join Us at the Stone Circle
                  </SolsticeSectionTitle>
                  <SolsticeParagraph style={{ textAlign: 'center', maxWidth: '52ch', margin: 0 }}>
                    We look forward to welcoming you as we gather with the Duchess of Rutland to
                    honour the light, celebrate the turning of the season, and share in a morning of
                    reflection, ritual and community at Belvoir Castle Estate.
                  </SolsticeParagraph>
                  <SolsticeBookButton
                    href={details.eventBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ maxWidth: '320px', marginTop: '0.75rem' }}
                  >
                    Book Your Place →
                  </SolsticeBookButton>
                </div>
              )}
            </SolsticeFullWidthContent>
          </>
        ) : (
        <EventContent>
          <MainContent>
            {/* About This Event - without white block */}
            <AboutSection>
              <MainSectionTitle>About This Event</MainSectionTitle>
              {details.eventStrapline && (
                <Strapline>{details.eventStrapline}</Strapline>
              )}
              <EventDescription dangerouslySetInnerHTML={{ __html: details.eventDescription }} />
            </AboutSection>
          </MainContent>

          <SideContent>
            {isSolstice ? (
              <SolsticePremiumInfoSection>
                <div style={{ marginBottom: '1.25rem' }}>
                  <SolsticeSectionDivider />
                  <p style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'rgba(16, 29, 64, 0.45)',
                    margin: '0 0 0.4rem',
                  }}>Event Details</p>
                  <p style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#101d40',
                    margin: 0,
                    lineHeight: 1.25,
                  }}>Summer Solstice Celebration</p>
                </div>

                <SolsticeInfoRow>
                  <span className="row-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </span>
                  <div className="row-content">
                    <div className="row-label">Dates</div>
                    <div className="row-value">Sunday 21 June 2026</div>
                  </div>
                </SolsticeInfoRow>

                <SolsticeInfoRow>
                  <span className="row-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                  </span>
                  <div className="row-content">
                    <div className="row-label">Location</div>
                    <div className="row-value">Belvoir Castle Estate</div>
                  </div>
                </SolsticeInfoRow>

                <SolsticeInfoRow>
                  <span className="row-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
                    </svg>
                  </span>
                  <div className="row-content">
                    <div className="row-label">Stone Circle Gathering</div>
                    <div className="row-value">Sun 21 June · 8:30am–11:30am</div>
                  </div>
                </SolsticeInfoRow>

                <SolsticeInfoRow>
                  <span className="row-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z"/>
                    </svg>
                  </span>
                  <div className="row-content">
                    <div className="row-label">Tea at Belvoir Lodge</div>
                    <div className="row-value">Sun 21 June · 12:00pm–1:00pm</div>
                  </div>
                </SolsticeInfoRow>

                {details.eventBookingUrl && (
                  <SolsticeBookButton
                    href={details.eventBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Your Place →
                  </SolsticeBookButton>
                )}

                <p style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '0.78rem',
                  color: 'rgba(16, 29, 64, 0.45)',
                  textAlign: 'center',
                  margin: '0.85rem 0 0',
                  lineHeight: 1.5,
                }}>
                  All are welcome. Come as you are.
                </p>
              </SolsticePremiumInfoSection>
            ) : (
              /* Standard Event Information Section */
              <InfoSection>
                <SectionTitle>Event Information</SectionTitle>
                
                {/* Event Details */}
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel><LocationIcon /> Location</InfoLabel>
                    <InfoValue>{details.eventLocation}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel><CalendarIcon /> Date</InfoLabel>
                    <InfoValue>{formattedDate}</InfoValue>
                  </InfoItem>
                  {details.eventTime && (
                    <InfoItem>
                      <InfoLabel><ClockIcon /> Time</InfoLabel>
                      <InfoValue>{details.eventTime}</InfoValue>
                    </InfoItem>
                  )}
                  {details.eventTicketTier && (
                    <InfoItem>
                      <InfoLabel><TicketIcon /> Ticket Tier</InfoLabel>
                      <InfoValue>{getTicketTierName(details.eventTicketTier)}</InfoValue>
                    </InfoItem>
                  )}
                  {getEventTags() && getEventTags().length > 0 && (
                    <InfoItem>
                      <InfoLabel><TagIcon /> Tags</InfoLabel>
                      <InfoValue>
                        <TagsContainer style={{ margin: 0, padding: 0 }}>
                          {getEventTags().map(tag => (
                            <Tag key={tag}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      </InfoValue>
                    </InfoItem>
                  )}
                </InfoGrid>
                
                {/* Ticket Prices */}
                {details.eventTicketPrices && (details.eventTicketPrices.adult || details.eventTicketPrices.child || details.eventTicketPrices.concession) && (
                  <>
                    <SectionTitle style={{ marginTop: '2rem' }}>Ticket Prices</SectionTitle>
                    <PriceGrid>
                      {details.eventTicketPrices.adult && (
                        <PriceCard>
                          <PriceType>Adult</PriceType>
                          <Price>£{details.eventTicketPrices.adult}</Price>
                        </PriceCard>
                      )}
                      {details.eventTicketPrices.child && (
                        <PriceCard>
                          <PriceType>Child</PriceType>
                          <Price>£{details.eventTicketPrices.child}</Price>
                        </PriceCard>
                      )}
                      {details.eventTicketPrices.concession && (
                        <PriceCard>
                          <PriceType>Concession</PriceType>
                          <Price>£{details.eventTicketPrices.concession}</Price>
                        </PriceCard>
                      )}
                    </PriceGrid>
                  </>
                )}
                
                {/* Member Benefit */}
                {id === 'cG9zdDoxMzU=' ? (
                  <MemberInfo>
                    <DangerText>Not included with Membership</DangerText>
                  </MemberInfo>
                ) : (
                  details.eventMemberIncluded && (
                    <MemberInfo>
                      <MemberBadge><MemberIcon /> Member Benefit</MemberBadge>
                      Included with membership
                    </MemberInfo>
                  )
                )}
                
                {/* Book Now Button */}
                {details.eventBookingUrl && (
                  <BookNowButton 
                    href={details.eventBookingUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Book Now
                  </BookNowButton>
                )}
                
                {/* Gift Voucher Button - Only for Garden Show */}
                {eventData.id === 'cG9zdDoxMjc=' && (
                  <GiftVoucherButton 
                    href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=6c6ed9ea-e1ba-4686-9707-1cd9f1e2b400" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Buy as a Gift Voucher
                  </GiftVoucherButton>
                )}
              </InfoSection>
            )}
          </SideContent>
        </EventContent>
        )}

        {/* Gallery Section */}
        {details.eventGalleryEnabled && details.eventGalleryImages && (
          <GallerySection>
            <SectionTitle>Event Gallery</SectionTitle>
            <GalleryMosaic>
              {Object.entries(details.eventGalleryImages)
                .filter(([key, value]) => key.startsWith('image') && value?.node?.sourceUrl)
                .map(([key, value]) => (
                  <GalleryImage 
                    key={key} 
                    src={value.node.sourceUrl}
                    alt={`Gallery image ${key}`}
                  />
                ))}
            </GalleryMosaic>
          </GallerySection>
        )}

        {/* Feature Sections */}
        {details.eventSection1Enabled && (
          <FeatureSection>
            <FeatureGrid>
              <FeatureImage 
                src={details.eventSection1Image?.node?.sourceUrl || getDefaultImageForLocation(details.location)} 
              />
              <FeatureContent>
                <FeatureTitle>{details.eventSection1Title}</FeatureTitle>
                <FeatureText dangerouslySetInnerHTML={{ __html: details.eventSection1Content }} />
              </FeatureContent>
            </FeatureGrid>
          </FeatureSection>
        )}

        {details.eventSection2Enabled && (
          <FeatureSection>
            <FeatureGrid imageRight>
              <FeatureContent imageRight>
                <FeatureTitle>{details.eventSection2Title}</FeatureTitle>
                <FeatureText dangerouslySetInnerHTML={{ __html: details.eventSection2Content }} />
              </FeatureContent>
              <FeatureImage 
                src={details.eventSection2Image?.node?.sourceUrl || getDefaultImageForLocation(details.location)} 
                imageRight
              />
            </FeatureGrid>
          </FeatureSection>
        )}

        {eventData.id === 'cG9zdDoxMjc=' && (
          <>
            <FeatureSection>
              <FeatureGrid>
                <FeatureImage src={paulaImage} />
                <FeatureContent>
                  <FeatureTitle>Meet Paula Routledge</FeatureTitle>
                  <FeatureText>
                    <p>We're excited to announce that Paula Routledge—respected horticulturist, floral designer, and RHS Floral Judge—will be joining us at this year's show!</p>
                    <p>Having studied horticulture at Askham Bryan College, Paula now runs Floral Media, a beautiful flower farm in Nottinghamshire, with her husband Steven. Their gardens are open for the National Garden Scheme and private group visits, offering a stunning showcase of seasonal blooms.</p>
                    <p>A Chelsea Flower Show medallist and North Midlands NAFAS demonstrator, Paula brings a wealth of floristry knowledge and garden expertise. Join us as she shares her creative techniques, floral insights, and stories from her inspiring journey in the world of horticulture and design—an engaging experience for garden and flower lovers alike.</p>
                  </FeatureText>
                </FeatureContent>
              </FeatureGrid>
            </FeatureSection>

            <FeatureSection>
              <FeatureGrid imageRight>
                <FeatureContent imageRight>
                  <FeatureTitle>Meet Karen Gimson</FeatureTitle>
                  <FeatureText>
                    <p>We're delighted to welcome Karen Gimson—experienced garden designer, horticultural speaker, and writer—to this year's show!</p>
                    <p>With over 25 years as a travelling head gardener and designer for large private gardens, Karen brings a deep well of knowledge and a passion for wildlife-friendly gardening. Karen is a regular columnist for Garden News Magazine and a familiar voice on BBC Radio Leicester's gardening show.</p>
                    <p>Karen's an ambassador for Rainbows Hospice and an active member of the Leicestershire National Garden Scheme team. She's also a familiar face at Belvoir Castle, where she's previously lead children's planting sessions and inspires the next generation of gardeners.</p>
                  </FeatureText>
                </FeatureContent>
                <FeatureImage src={kgimsonImage} imageRight />
              </FeatureGrid>
            </FeatureSection>
          </>
        )}

        {/* Extra hardcoded sections for event cG9zdDo0NDE= (4 sections total: 2 from WP + these 2) */}
        {eventData.id === 'cG9zdDo0NDE=' && (
          <>
            <FeatureSection>
              <FeatureGrid>
                <FeatureImage src="https://www.belvoircastle.com/wp/wp-content/uploads/2026/02/Alex-Mitchell-WEB-2025.png" />
                <FeatureContent>
                  <FeatureTitle>Alex Mitchell</FeatureTitle>
                  <FeatureText>
                    <p>Award-winning stand-up comedian, writer and actor Alex Mitchell is one of the fastest-rising names on the UK comedy circuit.</p>
                    <p>A queer, autistic and disabled comic, Alex brings brilliantly sharp observations and fearless honesty to every performance. In 2024, he was a finalist on Britain's Got Talent, with his performances amassing tens of thousands of online views. Since then, he has appeared on Good Morning Britain, Lorraine and BBC Sounds.</p>
                    <p>Following multiple critically acclaimed and sold-out runs at the Edinburgh Festival Fringe, Alex completed his debut UK tour in 2025. A powerhouse of punchlines with a warm, magnetic stage presence, he is a must-see live act.</p>
                  </FeatureText>
                </FeatureContent>
              </FeatureGrid>
            </FeatureSection>

            <FeatureSection>
              <FeatureGrid imageRight>
                <FeatureContent imageRight>
                  <FeatureTitle>Felicity Ward</FeatureTitle>
                  <FeatureText>
                    <p>International multi-award-winning stand-up comedian Felicity Ward is renowned for her no-filter honesty, electric stage presence and laugh-out-loud storytelling.</p>
                    <p>She has appeared on Live at the Apollo, Mock the Week, The Stand-Up Sketch Show and performs regularly on the world's biggest comedy stages. Felicity stars as Hannah Howard in the Australian version of The Office on Amazon Prime and appears in Time Bandits, produced by Taika Waititi.</p>
                    <p>A nominee for 'Best Comedy' at the Edinburgh Festival Fringe, Melbourne International Comedy Festival and Adelaide Fringe, and winner of multiple major awards, Felicity masterfully combines outrageous hilarity with sharp insight.</p>
                    <p>"Joyfully funny… her originality and sheer inventiveness stay with you long after." ★★★★★ – Edinburgh Evening News</p>
                  </FeatureText>
                </FeatureContent>
                <FeatureImage src="https://www.belvoircastle.com/wp/wp-content/uploads/2026/02/Felicity-Ward-WEB-2025.jpg" imageRight />
              </FeatureGrid>
            </FeatureSection>
          </>
        )}

        {renderTimeline()}

        {id === 'cG9zdDoxNTI=' && (
          <div style={{ margin: '4rem 0' }}>
            <NewsletterSignUp tag="Christmas 2025" />
          </div>
        )}

        {/* Global Image Slant Sections */}
        <ImageSlant 
          image={getDineSlantData().image}
          title={getDineSlantData().title}
          description={getDineSlantData().description}
          buttonText={getDineSlantData().buttonText}
          buttonAction={() => navigate(getDineSlantData().buttonLink)}
          imagePosition="left"
        />

        <ImageSlant 
          image={getShopSlantData().image}
          title={getShopSlantData().title}
          description={getShopSlantData().description}
          buttonText={getShopSlantData().buttonText}
          buttonAction={() => navigate(getShopSlantData().buttonLink)}
          imagePosition="right"
        />

        <ImageSlant 
          image={getStaySlantData().image}
          title={getStaySlantData().title}
          description={getStaySlantData().description}
          buttonText={getStaySlantData().buttonText}
          buttonAction={() => navigate(getStaySlantData().buttonLink)}
          imagePosition="left"
        />

        <BackButton onClick={() => navigate(-1)}>
          <BackIcon /> Back to Events
        </BackButton>
        <Footer />
      </EventContainer>
    </>
  );
};

export default EventDetail;